import UserSearchApiResponseDataDto from "../../types/dtos/generic/UserSearchApiResponseDataDto";
import UserSearchCriteria from "../../types/dtos/generic/UserSearchCriteria";
import SignedInUserDto, {
  SignedInUserUpdatedDetailsDto,
} from "../../types/dtos/users/SignedInUserDto";
import { UserProfileHoverDetails } from "../../types/dtos/users/UserProfileHoverDetails";
import ResultStatus from "../../types/dtos/generic/ResultStatus";
import { apiClient } from "../apiClient";

class userApi {
  constructor(authToken: string | undefined) {
    this.accessToken = authToken;
  }

  accessToken: string | undefined;

  /** Get the logged in user's details, which are stored in context and used throughout this app.
   * So this should only be used once, then the value referenced from the context
   */
  getLoggedInUserDetails(
    onSuccess: (user: SignedInUserDto) => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user/get-logged-in-user-details",
    ).then(
      (data) => {
        const signedInUser = data as SignedInUserDto;
        onSuccess(signedInUser);
      },
      (error) => {
        onError(error);
      },
    );
  }

  /** Get the logged in user's updated details */
  getRefreshedLoggedInUserDetails(
    onSuccess: (updates: SignedInUserUpdatedDetailsDto) => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user/get-updated-user-details",
    ).then(
      (data) => {
        const updates = data as SignedInUserUpdatedDetailsDto;
        onSuccess(updates);
      },
      (error) => {
        onError(error);
      },
    );
  }

  /** Get profile hover details for a user (e.g. for hover card in user management list). */
  getProfileHoverDetails(
    employeeId: string,
    onSuccess: (data: UserProfileHoverDetails) => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user/get-profile-hover-details?employeeId=${encodeURIComponent(employeeId)}`,
    ).then(
      (data) => {
        const details = data as UserProfileHoverDetails;
        onSuccess(details);
      },
      (error) => {
        onError(error);
      },
    );
  }

  /** Get the users from the search criteria */
  getUsersFromSearchCriteria(
    searchCriteria: UserSearchCriteria,
    onSuccess: (data: UserSearchApiResponseDataDto) => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user/get-users-from-search-criteria",
      {
        body: JSON.stringify(searchCriteria),
      },
    ).then(
      (data) => {
        onSuccess(data);
      },
      (error) => {
        onError(error);
      },
    );
  }

  /** Get the selected users from the 'GetUsersFromSearchCriteria' */
  getSelectedUsersAsDtos(
    ids: number[],
    includeExcludeMetaData: boolean,
    onSuccess: (data: UserSearchApiResponseDataDto) => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user/get-selected-users-as-dtos",
      {
        method: "POST",
        body: JSON.stringify({ ids, includeExcludeMetaData }),
      },
    ).then(
      (data) => {
        onSuccess(data);
      },
      (error) => {
        onError(error);
      },
    );
  }

  uploadProfilePicture(
    file: File,
    onSuccess: (filename: string) => void,
    onError: (error: any) => void,
  ) {
    const formData = new FormData();
    formData.append("image", file);

    return apiClient(
      this.accessToken,
      "dashboards/user/upload-profile-picture",
      {
        method: "POST",
        body: formData,
      },
    ).then(
      (response: ResultStatus) => {
        if (response.success) {
          onSuccess(response.data as string);
        } else {
          onError(response.message || "Image upload failed");
        }
      },
      (error) => {
        onError(error);
      },
    );
  }

  removeProfilePicture(onSuccess: () => void, onError: (error: any) => void) {
    return apiClient(
      this.accessToken,
      "dashboards/user/remove-profile-picture",
      {
        method: "PUT",
      },
    ).then(
      (response: ResultStatus) => {
        if (response.success) {
          onSuccess();
        } else {
          onError(response.message || "Failed to remove profile picture");
        }
      },
      (error) => {
        onError(error);
      },
    );
  }

  SetWalkThroughSeen(
    walkthrough: string,
    onSuccess: () => void,
    onError: (error: any) => void,
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user/set-walkthrough-seen?walkthroughType=${walkthrough}`,
    ).then(
      (data) => {
        onSuccess();
      },
      (error) => {
        onError(error);
      },
    );
  }
}

export default userApi;
